import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

type TypographyProps = {
  children: ReactNode
  className?: string
}

export function PageHeading({ children, className }: TypographyProps) {
  return (
    <h1
      className={cn(
        // Slightly smaller on mobile, original size on larger screens
        'scroll-m-20 text-2xl font-semibold tracking-tight text-balance md:text-4xl',
        className
      )}
    >
      {children}
    </h1>
  )
}

export function SectionHeading({ children, className }: TypographyProps) {
  return (
    <h2
      className={cn(
        'scroll-m-20 border-b pb-2 text-xl font-semibold tracking-tight first:mt-0 md:text-3xl',
        className
      )}
    >
      {children}
    </h2>
  )
}

export function SubsectionHeading({ children, className }: TypographyProps) {
  return (
    <h3
      className={cn(
        'scroll-m-20 text-lg font-semibold tracking-tight md:text-2xl',
        className
      )}
    >
      {children}
    </h3>
  )
}

export function MinorHeading({ children, className }: TypographyProps) {
  return (
    <h4
      className={cn(
        'scroll-m-20 text-sm font-semibold tracking-tight md:text-base',
        className
      )}
    >
      {children}
    </h4>
  )
}

export function BodyText({ children, className }: TypographyProps) {
  return <p className={cn('leading-7 not-first:mt-4', className)}>{children}</p>
}

export function QuoteText({ children, className }: TypographyProps) {
  return (
    <blockquote
      className={cn(
        'text-muted-foreground mt-6 border-l-2 pl-6 italic',
        className
      )}
    >
      {children}
    </blockquote>
  )
}

export function InlineCodeText({ children, className }: TypographyProps) {
  return (
    <code
      className={cn(
        'bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
        className
      )}
    >
      {children}
    </code>
  )
}

export function LeadText({ children, className }: TypographyProps) {
  return (
    <p className={cn('text-muted-foreground text-xl', className)}>{children}</p>
  )
}

export function EmphasisText({ children, className }: TypographyProps) {
  return (
    <div className={cn('text-lg font-semibold', className)}>{children}</div>
  )
}

export function LabelText({ children, className }: TypographyProps) {
  return (
    <small
      className={cn(
        'text-muted-foreground text-sm leading-none font-medium',
        className
      )}
    >
      {children}
    </small>
  )
}

export function MutedText({ children, className }: TypographyProps) {
  return (
    <p className={cn('text-muted-foreground text-sm', className)}>{children}</p>
  )
}
